import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import Select from 'react-select';
import { withDarkStyles } from '../utils/selectStyles';
import { matchesAllTokens } from '../utils/selectFilter';
import { nextDayCorrectedEnd } from '../utils/durationRule';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { API } from '../api/api';
import { transformTimelogsToEvents } from '../utils/events';

// ── helpers ──────────────────────────────────────────────────────────────────

const formatHrs = (decimal) => {
  if (!decimal || decimal <= 0) return '0:00';
  const h = Math.floor(decimal);
  const m = Math.round((decimal - h) * 60);
  return `${h}:${m.toString().padStart(2, '0')}`;
};

const parseFlexibleTime = (str) => {
  if (!str || !str.trim()) return 0;
  const t = str.trim();
  if (t.includes(',')) return parseFloat(t.replace(',', '.')) || 0;
  if (t.includes(':')) {
    const [h, m] = t.split(':').map(Number);
    return (h || 0) + (m || 0) / 60;
  }
  return parseFloat(t) || 0;
};

const extractAbbreviation = (name) => {
  const m = (name || '').match(/^\(([^)]+)\)/);
  return m ? `(${m[1]})` : name;
};

const getWeekStart = (date) => moment(date).startOf('isoWeek').toDate();
const getWeekDays = (start) => Array.from({ length: 7 }, (_, i) => moment(start).add(i, 'days').toDate());

let _rowId = 0;
const newRow = () => ({
  id: `r${++_rowId}`,
  projectId: '',
  timecodeId: '',
  projectTimecodes: [],
  entries: {}, // { 'YYYY-MM-DD': { hours, startTime, endTime, notes } }
});

// ── component ─────────────────────────────────────────────────────────────────

export default function WeeklyTimeLogModal({
  isOpen, onClose, projects, isDark, onEntrySaved, onEntryDeleted,
}) {
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [rows, setRows] = useState(() => [newRow(), newRow(), newRow(), newRow(), newRow()]);

  // Cell modal state
  const [cell, setCell] = useState(null); // { rowId, date, dateStr }
  const [cellHours, setCellHours] = useState('');
  const [cellStartTime, setCellStartTime] = useState('09:00');
  const [cellEndTime, setCellEndTime] = useState('');
  const [cellNotes, setCellNotes] = useState('');
  const [cellLastChanged, setCellLastChanged] = useState(null);
  const [cellError, setCellError] = useState('');

  // Bulk create state
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const weekDays = getWeekDays(weekStart);
  const weekNum = moment(weekStart).isoWeek();

  const navWeek = (dir) => setWeekStart(prev => moment(prev).add(dir, 'weeks').toDate());

  const loadProject = useCallback((rowId, projectId) => {
    if (!projectId) {
      setRows(prev => prev.map(r => r.id === rowId ? { ...r, projectTimecodes: [] } : r));
      return;
    }
    API.fetchTasksByProject(projectId)
      .then(data => {
        if (data.success) {
          setRows(prev => prev.map(r => r.id === rowId ? {
            ...r,
            projectTimecodes: data.data,
          } : r));
      }
      })
      .catch(() => {});
  }, []);

  const updateRow = (rowId, updates) =>
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, ...updates } : r));

  const addRow = () => setRows(prev => [...prev, newRow()]);
  const removeRow = (rowId) => setRows(prev => prev.filter(r => r.id !== rowId));

  // ── cell modal ──────────────────────────────────────────────────────────────

  const openCell = (rowId, day) => {
    const row = rows.find(r => r.id === rowId);
    if (!row?.projectId) return;
    const dateStr = moment(day).format('YYYY-MM-DD');
    const existing = row.entries[dateStr];

    setCell({ rowId, date: day, dateStr });
    setCellHours(existing ? formatHrs(existing.hours) : '');
    setCellStartTime(existing?.startTime || '09:00');
    setCellEndTime(existing?.endTime || '');
    setCellLastChanged(null);
    setCellError('');

    // Auto-fill notes with worktype abbreviation or existing notes
    if (existing?.notes) {
      setCellNotes(existing.notes);
    } else if (row.timecodeId) {
      const tc = row.projectTimecodes.find(t => t.id === row.timecodeId);
      setCellNotes(tc ? extractAbbreviation(tc.name) : '');
    } else {
      setCellNotes('');
    }
  };

  const closeCell = () => { setCell(null); setCellError(''); };

  // Sync start+end → hours when either time field changes
  useEffect(() => {
    if (!cell || (cellLastChanged !== 'startTime' && cellLastChanged !== 'endTime')) return;
    if (cellStartTime && cellEndTime) {
      const s = moment(`${cell.dateStr} ${cellStartTime}`, 'YYYY-MM-DD HH:mm');
      // Only a genuine midnight crossing moves to the next day. An end equal to
      // its start is a zero-length log, not 24 hours. See utils/durationRule.js.
      const e = nextDayCorrectedEnd(s, moment(`${cell.dateStr} ${cellEndTime}`, 'YYYY-MM-DD HH:mm'));
      const diff = e.diff(s, 'minutes') / 60;
      if (diff > 0 && diff <= 23) setCellHours(formatHrs(diff));
    }
  }, [cellStartTime, cellEndTime, cellLastChanged, cell]);

  const handleNotesChange = (e) => {
    const newValue = e.target.value.trimStart();
    const row = rows.find(r => r.id === cell?.rowId);
    const tc = row?.projectTimecodes.find(t => t.id === row?.timecodeId);

    if (tc) {
      const abbr = extractAbbreviation(tc.name);
      // Insert " | " separator when user types right after the abbreviation
      if (cellNotes === abbr && newValue.length > abbr.length && !newValue.includes(' | ')) {
        const userText = newValue.substring(abbr.length);
        setCellNotes(`${abbr} | ${userText}`);
        return;
      }
    }
    setCellNotes(newValue);
  };

  // Save to local state only (no API call yet)
  const saveCell = () => {
    if (!cell) return;
    const row = rows.find(r => r.id === cell.rowId);
    if (!row) return;

    if (!row.timecodeId) {
      setCellError('Please select a worktype for this row first');
      return;
    }

    const floatHours = parseFlexibleTime(cellHours);
    if (!floatHours || floatHours <= 0 || floatHours > 23) {
      setCellError('Please enter valid hours (e.g. 1:30 or 7.5)');
      return;
    }

    const startDate = moment(`${cell.dateStr} ${cellStartTime || '09:00'}`, 'YYYY-MM-DD HH:mm').toDate();
    const tc = row.projectTimecodes.find(t => t.id === row.timecodeId);
    const billingtype = tc?.billingtype === 'Non Billable' ? 'Non Billable' : 'Billable';

    const entry = {
      hours: floatHours,
      startTime: cellStartTime || '09:00',
      endTime: cellEndTime || moment(startDate).add(floatHours, 'hours').format('HH:mm'),
      billingtype,
      notes: cellNotes || '',
    };

    setRows(prev => prev.map(r => r.id === cell.rowId ? {
      ...r,
      entries: { ...r.entries, [cell.dateStr]: entry },
    } : r));
    closeCell();
  };

  const clearCell = (rowId, dateStr) => {
    setRows(prev => prev.map(r => r.id === rowId ? {
      ...r,
      entries: Object.fromEntries(Object.entries(r.entries).filter(([k]) => k !== dateStr)),
    } : r));
  };

  // ── bulk create ─────────────────────────────────────────────────────────────

  // Collect all valid, ready-to-submit entries
  const pendingEntries = rows.flatMap(row => {
    if (!row.projectId) return [];
    return weekDays.flatMap(day => {
      const dateStr = moment(day).format('YYYY-MM-DD');
      const entry = row.entries[dateStr];
      if (!entry) return [];
      return [{ row, dateStr, entry }];
    });
  });

  const handleCreate = async () => {
    if (pendingEntries.length === 0) return;
    setCreating(true);
    setCreateError('');

    // Build the payload expected by POST /api/timelogs/bulk
    const bulkEvents = pendingEntries.map(({ row, dateStr, entry }) => {
      const startDate = moment(`${dateStr} ${entry.startTime}`, 'YYYY-MM-DD HH:mm').toDate();
      const endDate = new Date(startDate.getTime() + entry.hours * 3600000);
      return {
        matterId: row.projectId,
        workTypeId: row.timecodeId || '',
        notes: entry.notes || '',
        billStatus: entry.billingtype,
        date: dateStr,
        start: startDate,
        end: endDate,
        sendToCapLegal: true,
      };
    });

    try {
      const data = await API.createTimelogsBulk({ events: bulkEvents });

      if (data.success) {
        const failures = data.failures || [];
        const failedIndexes = new Set(failures.map(f => f.index));

        // The response's `data` array is already in request order (failed
        // indexes simply omitted), so it lines up 1:1 with the successful
        // subset of pendingEntries.
        const createdEvents = transformTimelogsToEvents(data.data || []);
        createdEvents.forEach(ev => onEntrySaved?.(ev));

        // Clear only the cells that were successfully created; leave failed
        // ones in place so the user can fix and retry them.
        pendingEntries.forEach(({ row, dateStr }, i) => {
          if (!failedIndexes.has(i)) clearCell(row.id, dateStr);
        });

        if (failures.length > 0) {
          setCreateError(`${failures.length} of ${pendingEntries.length} entries failed to save`);
        } else {
          onClose();
        }
      } else {
        setCreateError('Bulk create failed — please try again');
      }
    } catch (err) {
      setCreateError(err.message || 'Error creating entries');
    } finally {
      setCreating(false);
    }
  };

  // ── derived values ──────────────────────────────────────────────────────────

  const dailyTotals = weekDays.map(day => {
    const ds = moment(day).format('YYYY-MM-DD');
    return rows.reduce((s, r) => s + (r.entries[ds]?.hours || 0), 0);
  });
  const grandTotal = dailyTotals.reduce((a, b) => a + b, 0);
  const activeRow = cell ? rows.find(r => r.id === cell.rowId) : null;
  const projectOptions = projects.map(p => ({ value: p.id, label: p.name }));

  const compactSelect = {
    control: (p) => ({ ...p, minHeight: '32px', maxHeight: '32px', fontSize: '12px', padding: '0' }),
    valueContainer: (p) => ({ ...p, padding: '0 6px', flexWrap: 'nowrap' }),
    indicatorsContainer: (p) => ({ ...p, height: '32px' }),
    placeholder: (p) => ({ ...p, fontSize: '12px', whiteSpace: 'nowrap' }),
    singleValue: (p) => ({ ...p, fontSize: '12px' }),
    input: (p) => ({ ...p, fontSize: '12px', margin: 0, padding: 0 }),
    menuPortal: (p) => ({ ...p, zIndex: 9999 }),
    menu: (p) => ({ ...p, zIndex: 9999, fontSize: '12px' }),
    option: (p) => ({ ...p, fontSize: '12px', padding: '6px 10px', cursor: 'pointer' }),
  };

  if (!isOpen) return null;

  const bg = isDark ? '#1c1c20' : '#ffffff';
  const bgSub = isDark ? '#242428' : '#f3f4f6';
  const bgCell = isDark ? '#2a2a30' : '#f9fafb';
  const border = isDark ? '#36363e' : '#e5e7eb';
  const textPrimary = isDark ? '#e5e7eb' : '#111827';
  const textMuted = isDark ? '#9ca3af' : '#6b7280';
  const inputBg = isDark ? '#242428' : '#ffffff';

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-60 z-[3000]" onClick={onClose} />

      {/* Modal shell */}
      <div
        className="fixed z-[3100] flex flex-col rounded-xl shadow-2xl overflow-hidden"
        style={{
          top: '3vh', bottom: '3vh', left: '1vw', right: '1vw',
          backgroundColor: bg, border: `1px solid ${border}`,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-3 flex-shrink-0 border-b" style={{ borderColor: border }}>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-lg font-semibold" style={{ color: textPrimary }}>Weekly Time Log</span>
            <div className="flex items-center gap-1">
              <button onClick={() => navWeek(-1)} className="p-1 rounded hover:opacity-70 transition-opacity" style={{ color: textMuted }}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="flex items-center gap-2 px-1">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: textMuted }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium" style={{ color: textPrimary }}>
                  {moment(weekDays[0]).format('DD/MM/YYYY')} to {moment(weekDays[6]).format('DD/MM/YYYY')}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#ccb778', color: '#fff' }}>
                  WEEK - {weekNum}
                </span>
              </div>
              <button onClick={() => navWeek(1)} className="p-1 rounded hover:opacity-70 transition-opacity" style={{ color: textMuted }}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {createError && (
              <span className="text-xs text-red-500 max-w-xs truncate">{createError}</span>
            )}
            <button
              onClick={handleCreate}
              disabled={creating || pendingEntries.length === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-opacity"
              style={{
                backgroundColor: '#ccb778',
                color: '#fff',
                opacity: (creating || pendingEntries.length === 0) ? 0.5 : 1,
                cursor: (creating || pendingEntries.length === 0) ? 'not-allowed' : 'pointer',
              }}
            >
              {creating ? 'Creating...' : (
                <>
                  Create
                  {pendingEntries.length > 0 && (
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold" style={{ backgroundColor: 'rgba(255,255,255,0.3)' }}>
                      {pendingEntries.length}
                    </span>
                  )}
                </>
              )}
            </button>
            <button onClick={onClose} className="p-1 rounded hover:opacity-70 transition-opacity" style={{ color: textMuted }}>
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="flex-1 overflow-auto">
          <table className="w-full border-collapse" style={{ minWidth: '880px' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <tr style={{ backgroundColor: bgSub, borderBottom: `1px solid ${border}` }}>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide" style={{ color: textMuted, width: '22%' }}>
                  Matter
                </th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide" style={{ color: textMuted, width: '18%' }}>
                  Worktype
                </th>
                {weekDays.map((day, i) => {
                  const isToday = moment(day).isSame(moment(), 'day');
                  return (
                    <th key={i} className="text-center px-1 py-2" style={{ width: '7%' }}>
                      <div className="text-xs font-semibold uppercase" style={{ color: isToday ? '#ccb778' : textMuted }}>
                        {moment(day).format('ddd D')}
                      </div>
                      <div className="text-xs mt-0.5" style={{ color: dailyTotals[i] > 0 ? '#ccb778' : textMuted }}>
                        {formatHrs(dailyTotals[i])}
                      </div>
                    </th>
                  );
                })}
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wide" style={{ color: grandTotal > 0 ? '#ccb778' : textMuted, width: '7%' }}>Total (hrs)</th>
                <th style={{ width: '3%' }}></th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, rowIdx) => {
                const rowTotal = weekDays.reduce((s, d) => {
                  const ds = moment(d).format('YYYY-MM-DD');
                  return s + (row.entries[ds]?.hours || 0);
                }, 0);
                const tcOptions = row.projectTimecodes.map(tc => ({ value: tc.id, label: tc.name }));
                const oddBg = rowIdx % 2 === 1 ? (isDark ? '#1f1f24' : '#f9fafb') : 'transparent';

                return (
                  <tr key={row.id} style={{ backgroundColor: oddBg, borderBottom: `1px solid ${border}` }}>
                    {/* Matter */}
                    <td className="px-2 py-2">
                      <Select
                        options={projectOptions}
                        filterOption={matchesAllTokens}
                        value={projectOptions.find(o => o.value === row.projectId) || null}
                        onChange={opt => {
                          updateRow(row.id, { projectId: opt?.value || '', timecodeId: '' });
                          if (opt?.value) loadProject(row.id, opt.value);
                          else setRows(prev => prev.map(r => r.id === row.id ? { ...r, projectTimecodes: [] } : r));
                        }}
                        placeholder="Select matter..."
                        isClearable
                        menuPortalTarget={document.body}
                        styles={withDarkStyles(compactSelect)}
                      />
                    </td>

                    {/* Worktype */}
                    <td className="px-2 py-2">
                      <Select
                        options={tcOptions}
                        filterOption={matchesAllTokens}
                        value={tcOptions.find(o => o.value === row.timecodeId) || null}
                        onChange={opt => updateRow(row.id, { timecodeId: opt?.value || '' })}
                        placeholder={row.projectId ? 'Select worktype...' : 'Select matter first...'}
                        isDisabled={!row.projectId}
                        isClearable
                        menuPortalTarget={document.body}
                        styles={withDarkStyles(compactSelect)}
                      />
                    </td>

                    {/* Day cells */}
                    {weekDays.map((day, di) => {
                      const ds = moment(day).format('YYYY-MM-DD');
                      const entry = row.entries[ds];
                      const isToday = moment(day).isSame(moment(), 'day');
                      const canClick = !!(row.projectId && row.timecodeId);
                      return (
                        <td key={di} className="px-1 py-2 text-center">
                          <button
                            onClick={() => openCell(row.id, day)}
                            disabled={!canClick}
                            title={canClick
                              ? (entry ? `Edit ${formatHrs(entry.hours)}` : 'Log time')
                              : 'Select a matter and worktype first'}
                            className="w-full rounded text-xs py-1 transition-all"
                            style={{
                              backgroundColor: entry
                                ? (isDark ? 'rgba(204,183,120,0.18)' : 'rgba(204,183,120,0.14)')
                                : (isToday ? (isDark ? '#242428' : '#eff6ff') : bgCell),
                              border: `1px solid ${entry ? '#ccb778' : (isToday ? (isDark ? '#3b82f6' : '#93c5fd') : border)}`,
                              color: entry ? '#ccb778' : textMuted,
                              cursor: canClick ? 'pointer' : 'not-allowed',
                              opacity: canClick ? 1 : 0.35,
                              fontWeight: entry ? 600 : 400,
                              minWidth: '44px',
                            }}
                          >
                            {entry ? formatHrs(entry.hours) : '—'}
                          </button>
                        </td>
                      );
                    })}

                    {/* Row total */}
                    <td className="text-right px-3 py-2 text-sm font-semibold" style={{ color: rowTotal > 0 ? '#ccb778' : textMuted }}>
                      {formatHrs(rowTotal)}
                    </td>

                    {/* Remove row */}
                    <td className="px-2 py-2 text-center">
                      <button
                        onClick={() => removeRow(row.id)}
                        title="Remove row"
                        className="p-1 rounded hover:opacity-70 transition-opacity"
                        style={{ color: textMuted }}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>

            <tfoot>
              <tr style={{ borderTop: `2px solid ${border}`, backgroundColor: bgSub }}>
                <td className="px-3 py-2" colSpan={2}>
                  <button
                    onClick={addRow}
                    className="flex items-center gap-1.5 text-sm font-medium hover:opacity-70 transition-opacity"
                    style={{ color: '#ccb778' }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Row
                  </button>
                </td>
                {weekDays.map((_, i) => (
                  <td key={i} className="text-center px-1 py-2 text-sm font-semibold" style={{ color: dailyTotals[i] > 0 ? '#ccb778' : textMuted }}>
                    {formatHrs(dailyTotals[i])}
                  </td>
                ))}
                <td className="text-right px-3 py-2 text-sm font-bold" style={{ color: grandTotal > 0 ? '#ccb778' : textMuted }}>
                  {formatHrs(grandTotal)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── Day Cell Modal ── */}
      {cell && activeRow && (
        <>
          <div className="fixed inset-0 z-[3200]" onClick={closeCell} />
          <div
            className="fixed z-[3300] rounded-xl shadow-2xl p-5"
            style={{
              top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              width: '340px',
              backgroundColor: bg, border: `1px solid ${border}`,
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-base" style={{ color: textPrimary }}>
                {moment(cell.date).format('dddd, D MMM YYYY')}
              </h3>
              <button onClick={closeCell} className="hover:opacity-70 transition-opacity" style={{ color: textMuted }}>
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Row context (read-only summary) */}
            <div className="mb-4 rounded-lg px-3 py-2 text-xs" style={{ backgroundColor: bgCell, color: textMuted }}>
              <div className="font-medium" style={{ color: textPrimary }}>
                {projects.find(p => p.id === activeRow.projectId)?.name || ''}
              </div>
              {activeRow.timecodeId && (
                <div className="mt-0.5">{activeRow.projectTimecodes.find(tc => tc.id === activeRow.timecodeId)?.name || ''}</div>
              )}
            </div>

            {/* Hours / Start / End */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: textMuted }}>Hours</label>
                <input
                  type="text"
                  value={cellHours}
                  onChange={e => { setCellHours(e.target.value.replace(/[^0-9:,.]/g, '')); setCellLastChanged('hours'); }}
                  onBlur={() => {
                    const parsed = parseFlexibleTime(cellHours);
                    if (parsed > 0 && parsed <= 23) {
                      setCellHours(formatHrs(parsed));
                      const s = moment(`${cell.dateStr} ${cellStartTime}`, 'YYYY-MM-DD HH:mm');
                      setCellEndTime(s.clone().add(parsed, 'hours').format('HH:mm'));
                    }
                  }}
                  placeholder="1:30"
                  className="w-full text-sm rounded-md border px-2 py-1.5"
                  style={{ backgroundColor: inputBg, borderColor: border, color: textPrimary }}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: textMuted }}>Start</label>
                <input
                  type="time"
                  value={cellStartTime}
                  onChange={e => { setCellStartTime(e.target.value); setCellLastChanged('startTime'); }}
                  className="w-full text-sm rounded-md border px-2 py-1.5"
                  style={{ backgroundColor: inputBg, borderColor: border, color: textPrimary }}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: textMuted }}>End</label>
                <input
                  type="time"
                  value={cellEndTime}
                  onChange={e => { setCellEndTime(e.target.value); setCellLastChanged('endTime'); }}
                  className="w-full text-sm rounded-md border px-2 py-1.5"
                  style={{ backgroundColor: inputBg, borderColor: border, color: textPrimary }}
                />
              </div>
            </div>

            {/* Notes — always shown; auto-filled with worktype abbreviation */}
            <div className="mb-3">
              <label className="block text-xs font-medium mb-1" style={{ color: textMuted }}>Notes</label>
              <input
                type="text"
                value={cellNotes}
                onChange={handleNotesChange}
                placeholder="(WKT) | description..."
                maxLength={255}
                className="w-full text-sm rounded-md border px-2 py-1.5"
                style={{ backgroundColor: inputBg, borderColor: border, color: textPrimary }}
              />
              <div className="text-right text-xs mt-0.5" style={{ color: textMuted }}>{cellNotes.length}/255</div>
            </div>

            {cellError && (
              <div className="mb-3 text-xs rounded-md px-3 py-2" style={{ backgroundColor: isDark ? '#3b1f1f' : '#fef2f2', color: '#ef4444' }}>
                {cellError}
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-2">
              <button
                onClick={saveCell}
                className="flex-1 py-2 rounded-lg text-sm font-semibold transition-opacity"
                style={{ backgroundColor: '#ccb778', color: '#fff' }}
              >
                {activeRow.entries[cell.dateStr] ? 'Update' : 'Add'}
              </button>
              {activeRow.entries[cell.dateStr] && (
                <button
                  onClick={() => { clearCell(cell.rowId, cell.dateStr); closeCell(); }}
                  className="px-3 py-2 rounded-lg text-sm transition-colors"
                  style={{ border: '1px solid #ef4444', color: '#ef4444' }}
                >
                  Remove
                </button>
              )}
              <button
                onClick={closeCell}
                className="px-3 py-2 rounded-lg text-sm transition-opacity hover:opacity-70"
                style={{ border: `1px solid ${border}`, color: textMuted }}
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
